import {
  ACTIVITIES_TYPE,
  LABS_TYPE,
  QUIZZES_TYPE,
  TEAMS_ENTITY,
  USERS_ENTITY,
  USERS_TYPE,
  VIDEOS_OVERALL_TYPE,
  VIDEOS_TYPE,
} from 'constants/analytics';
import { getTableCsvExport } from 'services/analyticsExports';

/**
 * Stable reason codes returned by the Teams API export endpoint on
 * failure. Mirrors the JSON envelope ``{"reason": "...", "detail": "..."}``.
 */
export type TableCsvExportFailureReason =
  | 'permission_denied'
  | 'request_failed'
  | 'no_data'
  | 'row_limit_exceeded'
  | 'file_size_exceeded'
  | 'upstream_analytics_failed'
  | 'unexpected_response_shape'
  | 'timeout';

export type TableCsvExportResult =
  | { ok: true; blob: Blob; filename: string }
  | { ok: false; reason: TableCsvExportFailureReason };

/**
 * Map the user's current chart/entity selection to the backend's allowlisted
 * ``export_type``. Returns ``null`` for combinations Phase 1 does not
 * support (courses entity), so the caller can disable the button.
 */
const EXPORT_TYPE_BY_CHART_AND_ENTITY: Record<string, Record<string, string>> = {
  [USERS_TYPE.VALUE.toLowerCase()]: {
    [USERS_ENTITY.VALUE]: 'users_table',
  },
  [ACTIVITIES_TYPE.VALUE.toLowerCase()]: {
    [USERS_ENTITY.VALUE]: 'activities_users_table',
    [TEAMS_ENTITY.VALUE]: 'activities_teams_table',
  },
  [LABS_TYPE.VALUE.toLowerCase()]: {
    [USERS_ENTITY.VALUE]: 'labs_users_table',
    [TEAMS_ENTITY.VALUE]: 'labs_teams_table',
  },
  [QUIZZES_TYPE.VALUE.toLowerCase()]: {
    [USERS_ENTITY.VALUE]: 'quizzes_users_table',
    [TEAMS_ENTITY.VALUE]: 'quizzes_teams_table',
  },
  [VIDEOS_TYPE.VALUE.toLowerCase()]: {
    [USERS_ENTITY.VALUE]: 'videos_users_table',
    [TEAMS_ENTITY.VALUE]: 'videos_teams_table',
  },
  [VIDEOS_OVERALL_TYPE.VALUE.toLowerCase()]: {
    [USERS_ENTITY.VALUE]: 'videos_users_table',
    [TEAMS_ENTITY.VALUE]: 'videos_teams_table',
  },
};

export function getExportType(
  currentChart: string | undefined,
  currentEntity: string | undefined
): string | null {
  if (!currentChart || !currentEntity) return null;
  return EXPORT_TYPE_BY_CHART_AND_ENTITY[currentChart]?.[currentEntity] ?? null;
}

/** Extract the filename from a ``Content-Disposition`` header. Accepts
 * both quoted (``filename="x.csv"``) and unquoted (``filename=x.csv``)
 * forms. Returns ``null`` if the header is absent or has no filename
 * token. */
export function extractFilenameFromContentDisposition(header: unknown): string | null {
  if (typeof header !== 'string') return null;
  const match = /filename="([^"]+)"/.exec(header) || /filename=([^;]+)/.exec(header);
  return match ? match[1].trim() : null;
}

/** Build a safe fallback filename for the downloaded CSV when the server
 * does not provide a ``Content-Disposition`` filename. */
export function buildTableCsvFilename(exportType: string): string {
  const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const safeName = exportType.toLowerCase().replace(/[^a-z0-9]+/g, '_');
  return `analytics_${safeName}_${date}.csv`;
}

/** Create a Blob URL, trigger a browser download, then revoke the URL. */
export function downloadCsvBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

const FAILURE_REASONS: readonly TableCsvExportFailureReason[] = [
  'permission_denied',
  'request_failed',
  'no_data',
  'row_limit_exceeded',
  'file_size_exceeded',
  'upstream_analytics_failed',
  'unexpected_response_shape',
  'timeout',
];

function isFailureReason(value: unknown): value is TableCsvExportFailureReason {
  return typeof value === 'string' && (FAILURE_REASONS as readonly string[]).includes(value);
}

async function parseReasonFromBlob(body: unknown): Promise<TableCsvExportFailureReason> {
  if (!(body instanceof Blob)) return 'request_failed';
  try {
    const text = await body.text();
    const parsed = JSON.parse(text);
    if (parsed && isFailureReason(parsed.reason)) return parsed.reason;
  } catch {
    // fall through to generic
  }
  return 'request_failed';
}

/**
 * Run a table CSV export end-to-end against the Teams API endpoint.
 * Returns a discriminated union so the caller can render the matching
 * notification copy without parsing any free-form messages.
 */
export async function runTableCsvExport(
  exportType: string,
  params: Record<string, unknown>
): Promise<TableCsvExportResult> {
  try {
    const response = await getTableCsvExport(exportType, params);
    const filename =
      extractFilenameFromContentDisposition(response.headers?.['content-disposition']) ??
      buildTableCsvFilename(exportType);
    return { ok: true, blob: response.data, filename };
  } catch (error) {
    const reason = await parseReasonFromBlob(
      (error as { response?: { data?: unknown } })?.response?.data
    );
    return { ok: false, reason };
  }
}
